import { useState, useEffect } from 'react';
import { contentService } from '../services/contentService';

/**
 * コンテンツ管理に関するロジックを処理するカスタムフック
 */
export const useContentManagement = (currentUser) => {
  const [contents, setContents] = useState([]);
  const [activeSubject, setActiveSubject] = useState('all');
  const [newContent, setNewContent] = useState({
    title: '',
    subject: '',
    randomizeQuizzes: false,
    timeLimitPerQuestion: 0,
    freeNavigation: false,
    timeLimitPerContent: 0,
    answerRevealMode: 'immediate',
    quickAnswerMode: 'standard',
  });
  const [formErrors, setFormErrors] = useState({});
  const [editingContentId, setEditingContentId] = useState(null);

  const isMaster = currentUser?.isMaster || false;

  // コンテンツの購読
  useEffect(() => {
    const unsubscribe = contentService.subscribeToContents(setContents);
    return unsubscribe;
  }, []);

  // フィルタリングされたコンテンツ
  const filteredContents = activeSubject === 'all'
    ? contents
    : contents.filter((content) => content.subject === activeSubject);

  // コンテンツの追加
  const handleAddContent = async () => {
    if (!isMaster) {
      throw new Error('コンテンツの追加権限がありません。');
    }

    const errors = {};
    if (!newContent.title.trim()) errors.title = 'タイトルを入力してください。';
    if (!newContent.subject) errors.subject = '教科を選択してください。';

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }

    try {
      const contentData = contentService.createContentData(newContent);
      await contentService.addContent(contentData);
      
      resetForm();
      return true;
    } catch (error) {
      console.error('コンテンツの追加に失敗しました:', error);
      throw error;
    }
  };

  // コンテンツの更新
  const handleUpdateContent = async () => {
    if (!isMaster) {
      throw new Error('コンテンツの更新権限がありません。');
    }

    const errors = {};
    if (!newContent.title.trim()) errors.title = 'タイトルを入力してください。';
    if (!newContent.subject) errors.subject = '教科を選択してください。';

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }

    try {
      const contentData = contentService.createContentData(newContent);
      await contentService.updateContent(editingContentId, contentData);
      
      resetForm();
      return true;
    } catch (error) {
      console.error('コンテンツの更新に失敗しました:', error);
      throw error;
    }
  };

  // コンテンツの削除
  const handleDeleteContent = async (contentId) => {
    if (!isMaster) {
      throw new Error('コンテンツの削除権限がありません。');
    }

    try {
      await contentService.deleteContent(contentId);
      return true;
    } catch (error) {
      console.error('コンテンツの削除に失敗しました:', error);
      throw error;
    }
  };

  // 編集開始
  const startEditContent = (content) => {
    if (!isMaster) {
      throw new Error('コンテンツの編集権限がありません。');
    }
    
    setEditingContentId(content.id);
    setNewContent({
      title: content.title,
      subject: content.subject,
      randomizeQuizzes: content.randomizeQuizzes || false,
      freeNavigation: content.freeNavigation || false,
      timeLimitPerQuestion: content.freeNavigation ? 0 : (content.timeLimitPerQuestion || 0),
      timeLimitPerContent: content.freeNavigation ? (content.timeLimitPerContent || 0) : 0,
      answerRevealMode: content.answerRevealMode || 'immediate',
      quickAnswerMode: content.quickAnswerMode || 'standard'
    });
  };

  // フォームリセット
  const resetForm = () => {
    setNewContent({
      title: '',
      subject: '',
      randomizeQuizzes: false,
      timeLimitPerQuestion: 0,
      freeNavigation: false,
      timeLimitPerContent: 0,
      answerRevealMode: 'immediate',
      quickAnswerMode: 'standard',
    });
    setEditingContentId(null);
    setFormErrors({});
  };

  return {
    // 状態
    contents,
    filteredContents,
    activeSubject,
    newContent,
    formErrors,
    editingContentId,
    isMaster,

    // アクション
    setActiveSubject,
    setNewContent,
    setFormErrors,
    handleAddContent,
    handleUpdateContent,
    handleDeleteContent,
    startEditContent,
    resetForm
  };
}; 